import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../../../lib/api';

/**
 * Loads the Best-PESO scoreboard for a month and saves the two writable surfaces:
 *  - the year-scoped award inputs (peso_scorecard) via PUT /api/peso/scoreboard
 *  - this month's numbers, written as SPRS overrides via PUT /api/peso/scoreboard/month
 *    (the same peso_report_drafts row the DOLE Reports SPRS editor uses)
 *
 * The server computes all points/goals, so this hook only owns load + the two saves, each
 * with its own optimistic-concurrency base timestamp (from meta) so one save never
 * collides with the other.
 */
export function useScoreboard(authHeaders, period) {
  const { t } = useTranslation();
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [savingYear, setSavingYear] = useState(false);
  const [savingMonth, setSavingMonth] = useState(false);

  const yearBase = useRef(null);   // scorecard updated_at
  const monthBase = useRef(null);  // SPRS draft updated_at for the current period

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const headers = await authHeaders();
      const res = await api.get('/api/peso/scoreboard', { headers, params: { period } });
      setBoard(res.data);
      yearBase.current = res.data.meta?.year_updated_at || null;
      monthBase.current = res.data.meta?.month_updated_at || null;
    } catch (e) {
      setLoadError(true);
      toast.error(e?.response?.data?.detail || t('peso.scoreboardLoadFailed', 'Failed to load scoreboard'));
    } finally {
      setLoading(false);
    }
  }, [authHeaders, period, t]);

  useEffect(() => { load(); }, [load]);

  const saveYear = useCallback(async (data) => {
    setSavingYear(true);
    try {
      const headers = await authHeaders();
      const year = period.slice(0, 4);
      const res = await api.put('/api/peso/scoreboard',
        { data, base_updated_at: yearBase.current },
        { headers, params: { year } });
      yearBase.current = res.data.updated_at;
      await load();
      return true;
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.warning(t('peso.scoreboardConflict', 'Someone else saved this — reloading their version.'));
        await load();
      } else {
        toast.error(e?.response?.data?.detail || t('peso.scoreboardSaveFailed', 'Failed to save'));
      }
      return false;
    } finally {
      setSavingYear(false);
    }
  }, [authHeaders, period, load, t]);

  const saveMonth = useCallback(async (numbers) => {
    setSavingMonth(true);
    try {
      const headers = await authHeaders();
      const res = await api.put('/api/peso/scoreboard/month',
        { numbers, base_updated_at: monthBase.current },
        { headers, params: { period } });
      monthBase.current = res.data.updated_at;
      await load();
      return true;
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.warning(t('peso.scoreboardConflict', 'Someone else saved this month — reloading their version.'));
        await load();
      } else {
        toast.error(e?.response?.data?.detail || t('peso.scoreboardSaveFailed', 'Failed to save'));
      }
      return false;
    } finally {
      setSavingMonth(false);
    }
  }, [authHeaders, period, load, t]);

  return { board, loading, loadError, savingYear, savingMonth, reload: load, saveYear, saveMonth };
}
